import type { MatchDomain, MatchEvidenceCandidate, MatchEvidenceSignal, MatchStatus } from './types';

/** Every signal `type` the matching engine sends that this card or its chips treat specially. */
export const MATCH_SIGNAL_TYPE = {
    SUBJECT_NAME: 'SUBJECT_NAME',
    COMPANY_NAME: 'COMPANY_NAME',
    FORMER_NAME: 'FORMER_NAME',
    GST_NAME: 'GST_NAME',
    DIRECTOR_NAME: 'DIRECTOR_NAME',
    ORG_NAME_EXACT: 'ORG_NAME_EXACT',
    REGISTERED_ADDRESS: 'REGISTERED_ADDRESS',
    GEO_STATE: 'GEO_STATE',
    DOB: 'DOB',
    GENDER: 'GENDER',
    IDENTIFIER: 'IDENTIFIER',
    LEGAL_FORM: 'LEGAL_FORM',
    INCORPORATION_DATE: 'INCORPORATION_DATE',
} as const;

/** Display text for each match status: the legend/row-mark label, and the verb a chip pairs with
 *  its signal's `status` -- never `effect`, since every name signal is emitted
 *  `effect: UNAVAILABLE` by design and would grey out the one chip the design shows green. */
export const MATCH_STATUS_TEXT: Record<MatchStatus, { legendLabel: string; chipVerb: string }> = {
    EXACT: { legendLabel: 'Exact match', chipVerb: 'Matched' },
    PARTIAL: { legendLabel: 'Partial match', chipVerb: 'Partial Match' },
    MISMATCH: { legendLabel: 'Mismatch', chipVerb: 'Mismatch' },
};

export const MATCH_STRENGTH_LABEL = 'Match Strength:';

/** "N signals compared", shown beside the pill; omitted entirely when the count is 0. */
export const matchSignalsComparedLabel = (count: number) => `${count} signal${count === 1 ? '' : 's'} compared`;

export const MATCH_NOT_EVALUATED_LABEL = 'Not evaluated';

/** No `matchEvidence` and no reason: a legacy finding, or one never routed through the engine. */
export const MATCH_NOT_EVALUATED_LEGACY_REASON = 'this finding came from the earlier name check and carries no match strength.';

/** `matchEvidenceAbsentReason` present: the engine was meant to score this finding but didn't. */
export const MATCH_NOT_EVALUATED_PENDING_REASON = 'the match check did not complete for this finding.';

export const MATCH_TABLE_HEADERS = {
    STATUS: 'Status',
    SIGNAL: 'Matched Signal',
    OURS: 'Data Available (On AICA)',
} as const;

/** THEIRS column header, by the matching engine's domain. Complete for every domain the engine
 *  sends, so the card reads correctly wherever it is mounted; today only the legal-cases drawer
 *  renders it (a regulatory hit shows chips instead). */
const MATCH_THEIRS_HEADER_BY_DOMAIN: Record<MatchDomain, string> = {
    LITIGATION: 'Matched Value (Legal Case)',
    REGULATORY: 'Matched Value (Watchlist Hit)',
};

/** Falls back to a domain-neutral label when the domain is absent or not one of the known ones. */
export const matchTheirsHeader = (domain?: MatchDomain) => (domain && MATCH_THEIRS_HEADER_BY_DOMAIN[domain]) || 'Matched Value';

export const MATCH_SHOW_MORE = 'Show more';
export const MATCH_SHOW_LESS = 'Show less';

/** Rows visible before the reader asks for the rest, matching the design. */
export const MATCH_ROWS_COLLAPSED = 4;

/** Chips shown before the rest collapse into a "+N more" pill -- capped so a registry as wide as
 *  the regulatory one (13 signal types) can never grow the match header past its fixed height. */
export const MATCH_SIGNAL_CHIPS_MAX = 4;

/** Hidden from the card by product request, not a data gap -- the engine still sends and
 *  compares this signal, so don't "fix" it back by re-adding it to the table. */
export const HIDDEN_MATCH_SIGNAL_TYPE = MATCH_SIGNAL_TYPE.INCORPORATION_DATE;

/** Never shown as a header chip: an informational comparison, not evidence of who the hit is --
 *  the design gives these no chip even when the engine compared them. */
export const MATCH_SIGNAL_CHIP_EXCLUDED_TYPES: ReadonlySet<string> = new Set([MATCH_SIGNAL_TYPE.LEGAL_FORM, HIDDEN_MATCH_SIGNAL_TYPE]);

/** LEGAL_FORM enum value -> its display label. LLP, OPC and HUF are deliberately absent -- they
 *  already read correctly as acronyms, and a generic title-case transform would turn them into
 *  "Llp" / "Opc" / "Huf". A value with no entry here (unmapped, or a future engine addition)
 *  renders verbatim rather than being mangled or blanked. */
export const LEGAL_FORM_VALUE_LABEL: Record<string, string> = {
    PRIVATE_LIMITED: 'Private Limited',
    PUBLIC_LIMITED: 'Public Limited',
    PARTNERSHIP_FIRM: 'Partnership Firm',
    CORPORATION: 'Corporation',
    TRUST: 'Trust',
    SOCIETY: 'Society',
    INDIVIDUAL: 'Individual',
    UNKNOWN: 'Unknown',
};

/** Humanises a LEGAL_FORM signal's raw enum value (the engine's literal, e.g. PUBLIC_LIMITED) for display; the stored value and every other
 *  signal's value pass through unchanged. */
export const formatMatchSignalValue = (type: string, value: string): string =>
    type === MATCH_SIGNAL_TYPE.LEGAL_FORM ? LEGAL_FORM_VALUE_LABEL[value] ?? value : value;

/** Signal `type` -> the noun its chip leads with. `IDENTIFIER` has no entry here: its noun comes
 *  from the signal's own `note` instead (see MatchSignalChips.tsx). */
export const MATCH_SIGNAL_TYPE_LABEL: Partial<Record<string, string>> = {
    [MATCH_SIGNAL_TYPE.SUBJECT_NAME]: 'Name',
    [MATCH_SIGNAL_TYPE.COMPANY_NAME]: 'Name',
    [MATCH_SIGNAL_TYPE.FORMER_NAME]: 'Former Name',
    [MATCH_SIGNAL_TYPE.GST_NAME]: 'GST Name',
    // The engine's wire label ("Director Name") reads wrong to a reviewer -- the row is any party
    // the record names that we recognise as connected to the borrower, not always a director.
    [MATCH_SIGNAL_TYPE.DIRECTOR_NAME]: 'Related Party',
    [MATCH_SIGNAL_TYPE.REGISTERED_ADDRESS]: 'Address',
    [MATCH_SIGNAL_TYPE.GEO_STATE]: 'State',
    [MATCH_SIGNAL_TYPE.DOB]: 'DOB',
    [MATCH_SIGNAL_TYPE.GENDER]: 'Gender',
};

/** Overrides the wire label for DIRECTOR_NAME so the card row reads the same as the chip noun
 *  above; every other signal's label passes through unchanged. */
export const formatMatchSignalLabel = (type: string, label: string): string =>
    type === MATCH_SIGNAL_TYPE.DIRECTOR_NAME ? MATCH_SIGNAL_TYPE_LABEL[MATCH_SIGNAL_TYPE.DIRECTOR_NAME] ?? label : label;

/** SUBJECT_NAME is the searched director on a director search; DIRECTOR_NAME the borrower's
 *  other directors named on the same record. */
const DIRECTOR_ROW_TYPES: ReadonlySet<string> = new Set([MATCH_SIGNAL_TYPE.SUBJECT_NAME, MATCH_SIGNAL_TYPE.DIRECTOR_NAME]);

/** The engine's similarity score (0-100) a PARTIAL director name must reach before the engine
 *  counts it as that director. The engine marks names PARTIAL well below this, so a PARTIAL
 *  status alone is not a match. Mirrors data-scripts `python/relevance/rules/signals.json`
 *  DIRECTOR_NAME.matchFloor (0.9 x 100); change both together. */
export const DIRECTOR_MATCH_FLOOR = 90;

type DirectorMatch = Pick<MatchEvidenceCandidate, 'ours' | 'theirs' | 'status'> & { isDeciding: boolean; score: number | null };

/** A pairing plus which signal it came from: DIRECTOR_NAME is what the verdict rests on, SUBJECT_NAME is display-only. */
type TrackedMatch = DirectorMatch & { fromDirectorName: boolean };

const isMatched = (status: MatchStatus | null) => status === 'EXACT' || status === 'PARTIAL';

/** A candidate the engine would itself count as this director: exact, the one that decided the
 *  row, or a partial at or above the floor. A mismatch is never a match, even when deciding. */
const isCountedCandidate = (candidate: MatchEvidenceCandidate) =>
    isMatched(candidate.status) &&
    (candidate.status === 'EXACT' || candidate.isDeciding || (candidate.score ?? -Infinity) >= DIRECTOR_MATCH_FLOOR);

/** Orders two pairings of the same name: exact first, then the deciding one, then the higher score. */
const pairingRank = (match: DirectorMatch) => [match.status === 'EXACT' ? 1 : 0, match.isDeciding ? 1 : 0, match.score ?? -Infinity];

const isStrongerPairing = (candidate: DirectorMatch, incumbent: DirectorMatch) => {
    const a = pairingRank(candidate);
    const b = pairingRank(incumbent);
    const firstDiff = a.findIndex((value, index) => value !== b[index]);
    return firstDiff !== -1 && a[firstDiff] > b[firstDiff];
};

/** On a name collision a DIRECTOR_NAME pairing beats a SUBJECT_NAME one outright -- an exact SUBJECT_NAME
 *  must not hide the partial the verdict rests on. Between two of a kind the stronger pairing wins,
 *  so the result never depends on row order. */
const supersedes = (candidate: TrackedMatch, incumbent: TrackedMatch) =>
    candidate.fromDirectorName !== incumbent.fromDirectorName ? candidate.fromDirectorName : isStrongerPairing(candidate, incumbent);

/**
 * Product-requested: every director the record names -- the searched director and each other
 * director the engine matched on the same case -- on one row, comma-separated, where the first of
 * them sat. The row reads exact only when every DIRECTOR_NAME match in it is: that signal is what
 * the verdict rests on, so the searched director's own SUBJECT_NAME match neither lifts nor lowers
 * the row. A director row that matched no one (e.g. only a mismatch) is left as its own row, and so
 * is the searched director's own row when no other director matched alongside it.
 */
export const mergeDirectorRows = (rows: MatchEvidenceSignal[]): MatchEvidenceSignal[] => {
    const matches: TrackedMatch[] = [];
    const merged = new Set<MatchEvidenceSignal>();
    rows.filter((row) => DIRECTOR_ROW_TYPES.has(row.type)).forEach((row) => {
        const fromDirectorName = row.type === MATCH_SIGNAL_TYPE.DIRECTOR_NAME;
        const found: DirectorMatch[] = row.candidates?.length
            ? row.candidates.filter(isCountedCandidate)
            : [{ ...row, isDeciding: false }].filter((self) => isMatched(self.status));
        if (!found.length) return;
        merged.add(row);
        found.forEach((match) => {
            const tracked = { ...match, fromDirectorName };
            const seen = matches.findIndex((existing) => existing.ours === tracked.ours);
            if (seen === -1) matches.push(tracked);
            else if (supersedes(tracked, matches[seen])) matches[seen] = tracked;
        });
    });
    const directorNameMatches = matches.filter((match) => match.fromDirectorName);
    if (!directorNameMatches.length) return rows;
    const first = rows.findIndex((row) => merged.has(row));
    const directorRow: MatchEvidenceSignal = {
        ...rows[first],
        type: MATCH_SIGNAL_TYPE.DIRECTOR_NAME,
        ours: matches.map((match) => match.ours).join(', '),
        theirs: matches.map((match) => match.theirs).join(', '),
        status: directorNameMatches.every((match) => match.status === 'EXACT') ? 'EXACT' : 'PARTIAL',
        note: '',
        candidates: undefined,
    };
    return rows.flatMap((row, index) => (index === first ? [directorRow] : merged.has(row) ? [] : [row]));
};

/**
 * Product-requested collapse of two rows that describe the same comparison: COMPANY_NAME
 * (informational, always the current name) and ORG_NAME_EXACT (the identity rule, current name
 * or a former one). In the common case they show identical values and the second reads as a
 * duplicate, so the table keeps only whichever one actually decided -- ORG_NAME_EXACT when it is
 * among the rows, COMPANY_NAME otherwise. Every other row (SUBJECT_NAME, FORMER_NAME, GST_NAME,
 * DIRECTOR_NAME included) passes through untouched -- deliberately narrower than
 * MATCH_SIGNAL_NAME_TYPES below, which drives a separate collapse for the header chips.
 */
export const collapseCompanyNameRows = (rows: MatchEvidenceSignal[]): MatchEvidenceSignal[] => {
    const hasOrgNameExact = rows.some((row) => row.type === MATCH_SIGNAL_TYPE.ORG_NAME_EXACT);
    return hasOrgNameExact ? rows.filter((row) => row.type !== MATCH_SIGNAL_TYPE.COMPANY_NAME) : rows;
};

/** Signals that can only rule a litigation case out: a matching date is no reason to think the case
 *  is the borrower's, so neither is ever shown. */
const LITIGATION_SUPPRESS_ONLY_TYPES: ReadonlySet<string> = new Set([MATCH_SIGNAL_TYPE.DOB, MATCH_SIGNAL_TYPE.INCORPORATION_DATE]);

/**
 * Whether a compared signal is shown, given the domain of the evidence it came from. Litigation
 * hides dates and any state that did not match -- a different state is normal, "not evidence
 * either way". `status` alone tells a shared state (EXACT) from a different one (none). Any other
 * domain, or none, shows whatever the engine compared.
 */
export const isSignalShown = (signal: MatchEvidenceSignal, domain?: MatchDomain): boolean => {
    if (domain !== 'LITIGATION') return true;
    if (LITIGATION_SUPPRESS_ONLY_TYPES.has(signal.type)) return false;
    return signal.type !== MATCH_SIGNAL_TYPE.GEO_STATE || isMatched(signal.status);
};

/** Every name-ish signal type a hit can carry. A hit can hold several at once (subject, company,
 *  a former name, a GST name); the chip row collapses the set to its single highest-scoring
 *  member rather than showing "Name" four times over. */
export const MATCH_SIGNAL_NAME_TYPES: ReadonlySet<string> = new Set([
    MATCH_SIGNAL_TYPE.SUBJECT_NAME,
    MATCH_SIGNAL_TYPE.COMPANY_NAME,
    MATCH_SIGNAL_TYPE.FORMER_NAME,
    MATCH_SIGNAL_TYPE.GST_NAME,
]);

export const matchSignalChipsOverflowLabel = (count: number) => `+${count} more`;
